import formatMediaObject from '../utils/formatters/format-media-object';
import { formatDietaryLabels } from './dietary-labels';
import { formatAllergens } from './product';

/**
 * Format the breadcrumbs for the grocery product page.
 *
 * @param {object} context
 * @param {object} attributes
 *
 * @returns {({text: string, to: string}|{active: boolean, text: string})[]}
 */
export const formatBreadcrumbsForProductPage = (context, attributes) => {
    const product = attributes.grocery_product;
    const category = product
        ?.category;

    return [
        {
            text: context.$snippet('home'),
            to: '/',
        },
        {
            text: context.$snippet('grocery'),
            to: context.$snippet('grocery_page_link'),
        },
        {
            text: category?.name,
            to: category?.page?.uri,
        },
        {
            text: product?.marketing_title,
            active: true,
        },
    ].filter(({ to, active }) => to || active);
};

/**
 * Format the nutrition facts for the grocery product page.
 *
 * @param {object} product
 *
 * @returns {{description: string, title: string}[]}
 */
export function formatNutritionFacts(product) {
    return [
        {
            title: this.$snippet('nutrition_energy', 'energy (kJ)'),
            description: product.energy,
        },
        {
            title: this.$snippet('nutrition_calories', 'energy (kcal)'),
            description: product.calories,
        },
        {
            title: this.$snippet('nutrition_fat', 'fat (g)'),
            description: product.fat,
        },
        {
            title: this.$snippet('nutrition_fat_saturated', 'of which saturates (g)'),
            description: product.fat_saturated,
        },
        {
            title: this.$snippet('nutrition_protein', 'protein (g)'),
            description: product.protein,
        },
        {
            title: this.$snippet('nutrition_carbs', 'carbohydrates (g)'),
            description: product.carbs,
        },
        {
            title: this.$snippet('nutrition_sugars', 'of which sugars (g)'),
            description: product.sugars,
        },
        {
            title: this.$snippet('nutrition_fibre', 'fibre (g)'),
            description: product.fibre,
        },
        {
            title: this.$snippet('nutrition_salt', 'salt (g)'),
            description: product.salt,
        },
        {
            title: this.$snippet('nutrition_plant_points', 'plant points'),
            description: product.plant_points,
        },
    ].filter(({ description }) => description);
}

/**
 * Format the retailers for display on the grocery product page.
 *
 * @param {object} context
 * @param {Array} retailers
 *
 * @returns {{name: string, link: string, logo: {alt: string, src: string, width: number, height: number}, id: number}[]}
 */
const formatRetailers = (context, retailers = []) => {
    return retailers.map(({ id, link, retailer }) => ({
        id,
        name: retailer?.name,
        link,
        logo: formatMediaObject.call(context, retailer?.logo),
    }));
};

/**
 * Format the product data to be passed to the ProductPage component.
 *
 * @param {object} context
 * @param {object} attributes
 *
 * @returns {object}
 */
export const formatDataForGroceryProductPage = (context, attributes) => {
    const product = attributes.grocery_product;
    const category = product
        ?.category;

    return {
        category: {
            text: category?.name,
            to: category?.page?.uri,
        },
        name: product?.marketing_title,
        description: product?.marketing_product_description,
        calories: product?.calories_text || `${ product?.calories } kcal`,
        weight: product?.weight,
        image: formatMediaObject.call(context, product?.website_product_image),
        imageSm: formatMediaObject.call(context, product?.website_product_image),
        dietaryLabels: formatDietaryLabels(context, product?.dietary_labels),
        contains: formatAllergens(product?.allergens),
        allergenWarning: context.$snippet('grocery_allergens_text'),
        mayContain: formatAllergens(product?.may_contain),
        nutritionText: attributes.nutrition_text,
        nutritionFacts: formatNutritionFacts.call(context, product),
        ingredients: attributes.ingredients,
        ingredientsDisclaimer: attributes.ingredients_disclaimer,
        storyTitle: attributes.groceryPageTemplate_story_title,
        storyContent: attributes.groceryPageTemplate_story_content,
        howToEnjoyTitle: context.$snippet('how_to_enjoy'),
        howToEnjoyContent: attributes.how_to_enjoy,
        heroButtonLink: context.$snippet('where_to_buy_link'),
        heroButtonText: context.$snippet('where_to_buy'),
        retailers: formatRetailers(context, attributes.product_retailers),
        readMore: {
            buttonText: attributes.read_more?.button_text,
            content: attributes.read_more?.content,
        },
        plantPoints: product?.plant_points ? {
            points: product?.plant_points,
            description: product?.plant_points_description,
            image: formatMediaObject.call(context, product?.plant_points_image),
            background: product?.plant_points_background,
        } : {},
    };
};
